/** Shared customer search helpers for Client Panel + Global Search. */

export type CustomerSearchCategory =
  | 'all'
  | 'company'
  | 'individual'
  | 'email'
  | 'phone'
  | 'sage';

export const CUSTOMER_SEARCH_CATEGORIES: { value: CustomerSearchCategory; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'company', label: 'Company' },
  { value: 'individual', label: 'Individual' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'sage', label: 'Sage' },
];

/** How many DB rows to pull before client-side ranking when searching. */
export const CUSTOMER_SEARCH_CANDIDATE_LIMIT = 400;

export function phoneDigitsOnly(value: string | null | undefined): string {
  return String(value || '').replace(/\D/g, '');
}

export function looksLikePhoneQuery(term: string): boolean {
  const digits = phoneDigitsOnly(term);
  return digits.length >= 6 && digits.length >= term.replace(/\s+/g, '').length * 0.7;
}

export function sanitizeSearchTerm(term: string): string {
  return term.trim().replace(/,/g, ' ').replace(/\s+/g, ' ');
}

/** Split into meaningful tokens (drop tiny connectors). */
export function searchTokens(term: string): string[] {
  const safe = sanitizeSearchTerm(term);
  if (!safe) return [];
  return safe
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 1 && !/^(and|or|the|of)$/i.test(t));
}

/**
 * PostgREST `.or()` values: escape LIKE wildcards and quote when reserved chars appear.
 * Returns the wrapped pattern including leading/trailing % for ilike.
 */
export function escapeIlikePattern(raw: string): string {
  const escaped = String(raw || '')
    .replace(/\\/g, '\\\\')
    .replace(/%/g, '\\%')
    .replace(/_/g, '\\_');
  const pattern = `%${escaped}%`;
  // PostgREST reserved in filter values: , . : ( ) "
  if (/[,.:()"]/.test(escaped)) {
    return `"${pattern.replace(/"/g, '\\"')}"`;
  }
  return pattern;
}

function ilikePart(column: string, token: string): string {
  return `${column}.ilike.${escapeIlikePattern(token)}`;
}

function partsForToken(
  token: string,
  columns: string[]
): string[] {
  return columns.map((col) => ilikePart(col, token));
}

const ALL_TEXT_COLS = [
  'company_name',
  'industry',
  'first_name',
  'surname',
  'email_address',
  'sage_code',
  'contact_phone_number',
  'mobile_phone',
];

/**
 * Build PostgREST `.or()` clause for customer list/search by category.
 * Multi-word queries match each token across fields and also first+surname pairs.
 */
export function buildCustomerSearchOrClause(
  term: string,
  category: CustomerSearchCategory = 'all'
): string {
  const safe = sanitizeSearchTerm(term);
  if (!safe) return '';
  const tokens = searchTokens(safe);
  if (tokens.length === 0) return '';

  switch (category) {
    case 'company':
      return tokens.flatMap((t) => partsForToken(t, ['company_name'])).join(',');
    case 'individual': {
      const parts = tokens.flatMap((t) => partsForToken(t, ['first_name', 'surname']));
      if (tokens.length >= 2) {
        const a = tokens[0];
        const b = tokens[tokens.length - 1];
        // PostgREST and() inside or: and(first_name.ilike.*a*,surname.ilike.*b*)
        parts.push(
          `and(${ilikePart('first_name', a)},${ilikePart('surname', b)})`,
          `and(${ilikePart('first_name', b)},${ilikePart('surname', a)})`
        );
      }
      return parts.join(',');
    }
    case 'email':
      return tokens.flatMap((t) => partsForToken(t, ['email_address'])).join(',');
    case 'phone': {
      const parts = tokens.flatMap((t) =>
        partsForToken(t, ['contact_phone_number', 'mobile_phone'])
      );
      const digits = phoneDigitsOnly(safe);
      if (digits.length >= 6) {
        parts.push(ilikePart('contact_phone_number', digits));
        parts.push(ilikePart('mobile_phone', digits));
      }
      return parts.join(',');
    }
    case 'sage':
      return tokens.flatMap((t) => partsForToken(t, ['sage_code'])).join(',');
    case 'all':
    default: {
      const parts = tokens.flatMap((t) => partsForToken(t, ALL_TEXT_COLS));
      // Full phrase on company / email
      if (tokens.length > 1) {
        parts.push(ilikePart('company_name', safe));
        parts.push(ilikePart('email_address', safe));
        const a = tokens[0];
        const b = tokens[tokens.length - 1];
        parts.push(
          `and(${ilikePart('first_name', a)},${ilikePart('surname', b)})`,
          `and(${ilikePart('first_name', b)},${ilikePart('surname', a)})`
        );
      }
      const digits = phoneDigitsOnly(safe);
      if (digits.length >= 6) {
        parts.push(ilikePart('contact_phone_number', digits));
        parts.push(ilikePart('mobile_phone', digits));
      }
      return parts.join(',');
    }
  }
}

export function customerTypeFilterForCategory(
  category: CustomerSearchCategory
): 'company' | 'individual' | null {
  if (category === 'company') return 'company';
  if (category === 'individual') return 'individual';
  return null;
}

type RankableCustomer = {
  customerType?: string | null;
  companyName?: string | null;
  firstName?: string | null;
  surname?: string | null;
  emailAddress?: string | null;
  contactPhoneNumber?: string | null;
  mobilePhone?: string | null;
  sageCode?: string | null;
  industry?: string | null;
  generatedCertificates?: Array<{ attendeeName?: string; courseName?: string } | null> | null;
};

/** Match "Davor Kraljevic" style person names to a CRM customer row. */
export function resolveCustomerByPersonName<
  T extends {
    id: string;
    customerType?: string | null;
    companyName?: string | null;
    firstName?: string | null;
    surname?: string | null;
  }
>(personName: string, customers: T[]): T | undefined {
  const full = sanitizeSearchTerm(personName).toLowerCase();
  if (!full) return undefined;
  const parts = searchTokens(full);
  const first = parts[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1] : '';

  const scoreOne = (c: T): number => {
    const cf = String(c.firstName || '').trim().toLowerCase();
    const cs = String(c.surname || '').trim().toLowerCase();
    const cn = `${cf} ${cs}`.trim();
    if (cn && cn === full) return 1;
    if (first && last && cf === first && cs === last) return 2;
    if (first && last && cf.startsWith(first) && cs.startsWith(last)) return 3;
    if (first && !last && (cf.startsWith(first) || cs.startsWith(first))) return 8;
    return 999;
  };

  const ranked = [...customers]
    .map((c) => ({ c, s: scoreOne(c) }))
    .filter((x) => x.s < 999)
    .sort((a, b) => {
      const aInd = a.c.customerType === 'individual' || !a.c.companyName ? 0 : 1;
      const bInd = b.c.customerType === 'individual' || !b.c.companyName ? 0 : 1;
      return a.s - b.s || aInd - bInd;
    });
  return ranked[0]?.c;
}

/** True if any certificate attendee / course name matches the search term. */
export function customerMatchesCertificateSearch(
  customer: RankableCustomer,
  term: string
): boolean {
  const q = sanitizeSearchTerm(term).toLowerCase();
  if (!q || !Array.isArray(customer.generatedCertificates)) return false;
  const tokens = searchTokens(q);
  return customer.generatedCertificates.some((cert) => {
    if (!cert) return false;
    const hay = `${cert.attendeeName || ''} ${cert.courseName || ''}`.toLowerCase();
    if (!hay.includes(q) && !tokens.every((t) => hay.includes(t))) return false;
    return true;
  });
}

function fieldStartsWith(field: string | null | undefined, q: string): boolean {
  const f = String(field || '')
    .trim()
    .toLowerCase();
  return f.startsWith(q);
}

/** True if any whitespace-separated word in the field starts with q. */
function fieldHasWordPrefix(field: string | null | undefined, q: string): boolean {
  const f = String(field || '')
    .trim()
    .toLowerCase();
  if (!f || !q) return false;
  if (f.startsWith(q)) return true;
  return f.split(/[\s\-_/.,]+/).some((w) => w.startsWith(q));
}

function fieldIncludes(field: string | null | undefined, q: string): boolean {
  return String(field || '')
    .toLowerCase()
    .includes(q);
}

function fullName(customer: RankableCustomer): string {
  return `${customer.firstName || ''} ${customer.surname || ''}`.trim().toLowerCase();
}

function tokenMatchesCustomer(customer: RankableCustomer, token: string): boolean {
  const q = token.toLowerCase();
  if (!q) return true;
  const digits = phoneDigitsOnly(q);
  const phoneDigits = phoneDigitsOnly(
    customer.contactPhoneNumber || customer.mobilePhone || ''
  );
  return (
    fieldIncludes(customer.companyName, q) ||
    fieldIncludes(customer.firstName, q) ||
    fieldIncludes(customer.surname, q) ||
    fieldIncludes(customer.emailAddress, q) ||
    fieldIncludes(customer.sageCode, q) ||
    fieldIncludes(customer.industry, q) ||
    fieldIncludes(customer.contactPhoneNumber, q) ||
    fieldIncludes(customer.mobilePhone, q) ||
    (digits.length >= 6 && phoneDigits.includes(digits)) ||
    fullName(customer).includes(q) ||
    customerMatchesCertificateSearch(customer, q)
  );
}

/**
 * Lower score = better. Multi-word queries require every token to match somewhere
 * (or first+last / company phrase). Mid-word-only matches are demoted, not dropped.
 */
export function scoreCustomerMatch(
  customer: RankableCustomer,
  term: string,
  category: CustomerSearchCategory = 'all'
): number {
  const q = sanitizeSearchTerm(term).toLowerCase();
  if (!q) return 9999;

  const tokens = searchTokens(q);
  const digits = phoneDigitsOnly(q);
  const company = customer.companyName || '';
  const first = customer.firstName || '';
  const last = customer.surname || '';
  const email = customer.emailAddress || '';
  const phone = customer.contactPhoneNumber || customer.mobilePhone || '';
  const sage = customer.sageCode || '';
  const phoneDigits = phoneDigitsOnly(phone);
  const name = fullName(customer);

  if (category === 'phone' || (category === 'all' && looksLikePhoneQuery(term))) {
    if (digits.length >= 6 && phoneDigits.includes(digits)) return 5;
    if (String(phone).toLowerCase().includes(q)) return 40;
    // Still allow if every token matched elsewhere when not phone-only intent
    if (category === 'all' && tokens.every((t) => tokenMatchesCustomer(customer, t))) return 200;
    return 9000;
  }

  if (category === 'email' || (q.includes('@') && category === 'all')) {
    if (email.toLowerCase().startsWith(q)) return 10;
    if (email.toLowerCase().includes(q)) return 30;
    if (tokens.every((t) => tokenMatchesCustomer(customer, t))) return 200;
    return 9000;
  }

  if (category === 'sage') {
    if (fieldStartsWith(sage, q)) return 10;
    if (String(sage).toLowerCase().includes(q)) return 40;
    if (tokens.every((t) => tokenMatchesCustomer(customer, t))) return 200;
    return 9000;
  }

  // Multi-token: every token must hit somewhere
  if (tokens.length > 1) {
    const allHit = tokens.every((t) => tokenMatchesCustomer(customer, t));
    if (!allHit) return 9000;
    if (name.startsWith(q) || name.includes(q)) return 2;
    if (fieldStartsWith(company, q) || fieldIncludes(company, q)) return 3;
    if (
      (fieldHasWordPrefix(first, tokens[0]) && fieldHasWordPrefix(last, tokens[tokens.length - 1])) ||
      (fieldHasWordPrefix(first, tokens[tokens.length - 1]) && fieldHasWordPrefix(last, tokens[0]))
    ) {
      return 1;
    }
    return 15;
  }

  if (category === 'company') {
    if (fieldStartsWith(company, q)) return 1;
    if (fieldHasWordPrefix(company, q)) return 5;
    if (fieldIncludes(company, q)) return 80;
    if (customerMatchesCertificateSearch(customer, q)) return 90;
    return 9000;
  }

  if (category === 'individual') {
    if (fieldStartsWith(first, q)) return 1;
    if (fieldHasWordPrefix(first, q)) return 3;
    if (fieldStartsWith(last, q)) return 10;
    if (fieldHasWordPrefix(last, q)) return 15;
    if (fieldIncludes(first, q) || fieldIncludes(last, q) || name.includes(q)) return 80;
    // Only count cert hits on individual profiles (not company holders)
    if (
      (customer.customerType === 'individual' || (!customer.companyName && (first || last))) &&
      customerMatchesCertificateSearch(customer, q)
    ) {
      return 90;
    }
    return 9000;
  }

  // category === 'all', single token
  if (fieldStartsWith(first, q) || fieldStartsWith(company, q)) return 1;
  if (fieldHasWordPrefix(first, q) || fieldHasWordPrefix(company, q)) return 5;
  if (fieldStartsWith(last, q)) return 20;
  if (fieldHasWordPrefix(last, q)) return 25;
  if (digits.length >= 6 && phoneDigits.includes(digits)) return 50;
  if (fieldStartsWith(sage, q) || String(sage).toLowerCase().includes(q)) return 60;
  if (email.toLowerCase().includes(q)) return 70;
  if (fieldIncludes(first, q) || fieldIncludes(last, q) || fieldIncludes(company, q) || name.includes(q)) {
    return 80;
  }
  if (customerMatchesCertificateSearch(customer, q)) return 90;
  if (String(phone).toLowerCase().includes(q) || String(customer.industry || '').toLowerCase().includes(q)) {
    return 100;
  }
  return 9000;
}

export function rankCustomersBySearch<T extends RankableCustomer>(
  customers: T[],
  term: string,
  category: CustomerSearchCategory = 'all'
): T[] {
  const q = sanitizeSearchTerm(term);
  if (!q) return customers;
  return [...customers]
    .map((c, idx) => ({ c, idx, score: scoreCustomerMatch(c, q, category) }))
    .filter((x) => x.score < 9000)
    .sort((a, b) => a.score - b.score || a.idx - b.idx)
    .map((x) => x.c);
}

/** Local filter used while typing short terms or refining a loaded page. */
export function customerMatchesLocalSearch(
  customer: RankableCustomer & {
    clientDisplayEmail?: string;
    clientDisplayPhone?: string;
  },
  term: string,
  category: CustomerSearchCategory = 'all'
): boolean {
  const q = sanitizeSearchTerm(term).toLowerCase();
  if (!q) return true;
  return (
    scoreCustomerMatch(
      {
        ...customer,
        emailAddress: customer.emailAddress || (customer as any).clientDisplayEmail,
        contactPhoneNumber: customer.contactPhoneNumber || (customer as any).clientDisplayPhone,
      },
      q,
      category
    ) < 9000
  );
}
